/**
 * Global SEO Service
 *
 * This service manages global SEO data from Strapi's Global single type.
 * It provides default SEO values that can be used as fallbacks across the application.
 */

import { query } from "../lib/strapi";

export interface GlobalSEOData {
  defaultSeo?: {
    title?: string;
    description?: string;
    image?: string;
    keywords?: string;
    structuredData?: any;
    openGraph?: any;
    noIndex?: boolean;
    canonicalUrl?: string;
  };
}

export interface GlobalSiteData {
  id: number;
  siteName: string;
  siteDescription: string;
  defaultSeo?: GlobalSEOData["defaultSeo"];
  contactInfo?: {
    phone?: string;
    email?: string;
    address?: string;
  };
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    youtube?: string;
  };
}

class GlobalSEOService {
  private globalData: GlobalSiteData | null = null;
  private lastFetch: number = 0;
  private cacheTimeout: number = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch global SEO data from Strapi
   */
  async fetchGlobalData(): Promise<GlobalSiteData | null> {
    const now = Date.now();

    // Return cached data if it's still fresh
    if (this.globalData && now - this.lastFetch < this.cacheTimeout) {
      return this.globalData;
    }

    try {
      const response = await query("global", {
        method: "GET",
      });

      if (response?.data) {
        this.globalData = response.data;
        this.lastFetch = now;
        console.log("Global SEO data fetched successfully:", this.globalData);
      } else if (response) {
        // Handle case where data is at root level
        this.globalData = response;
        this.lastFetch = now;
        console.log(
          "Global SEO data fetched successfully (root level):",
          this.globalData
        );
      }

      return this.globalData;
    } catch (error) {
      console.error("Failed to fetch global SEO data:", error);
      return null;
    }
  }

  /**
   * Get global default SEO data
   */
  async getDefaultSEO(): Promise<GlobalSEOData["defaultSeo"] | null> {
    const globalData = await this.fetchGlobalData();
    return globalData?.defaultSeo || null;
  }

  /**
   * Get site name with fallback
   */
  async getSiteName(): Promise<string> {
    const globalData = await this.fetchGlobalData();
    return globalData?.siteName || "Evefant";
  }

  /**
   * Get site description with fallback
   */
  async getSiteDescription(): Promise<string> {
    const globalData = await this.fetchGlobalData();
    return (
      globalData?.siteDescription ||
      "Encuentra todo para tu fiesta y nosotros te lo llevamos. Desde decoración con globos, hasta los bocaditos. Delivery en todo Lima."
    );
  }

  /**
   * Get contact information
   */
  async getContactInfo(): Promise<GlobalSiteData["contactInfo"] | null> {
    const globalData = await this.fetchGlobalData();
    return globalData?.contactInfo || null;
  }

  /**
   * Get social media links
   */
  async getSocialMedia(): Promise<GlobalSiteData["socialMedia"] | null> {
    const globalData = await this.fetchGlobalData();
    return globalData?.socialMedia || null;
  }

  /**
   * Merge content-specific SEO with global defaults
   */
  async mergeWithGlobalSEO(contentSEO: any): Promise<any> {
    const globalSEO = await this.getDefaultSEO();

    if (!globalSEO) {
      return contentSEO;
    }

    // Merge with global defaults as fallbacks
    return {
      title: contentSEO?.title || globalSEO.title,
      description: contentSEO?.description || globalSEO.description,
      image: contentSEO?.image || globalSEO.image,
      keywords: contentSEO?.keywords || globalSEO.keywords,
      structuredData: contentSEO?.structuredData || globalSEO.structuredData,
      openGraph: {
        ...globalSEO.openGraph,
        ...contentSEO?.openGraph, // Content-specific OpenGraph takes precedence
      },
      noIndex:
        contentSEO?.noIndex !== undefined
          ? contentSEO.noIndex
          : globalSEO.noIndex,
      canonicalUrl: contentSEO?.canonicalUrl || globalSEO.canonicalUrl,
    };
  }

  /**
   * Get enhanced structured data with global organization info
   */
  async getEnhancedStructuredData(baseSchema: any): Promise<any> {
    const globalData = await this.fetchGlobalData();
    const contactInfo = globalData?.contactInfo;
    const socialMedia = globalData?.socialMedia;

    // Enhance the base schema with global organization data
    const enhancedSchema = {
      ...baseSchema,
    };

    // Add organization data if available
    if (contactInfo || socialMedia) {
      enhancedSchema.publisher = {
        "@type": "Organization",
        name: await this.getSiteName(),
        description: await this.getSiteDescription(),
        ...(contactInfo?.phone && { telephone: contactInfo.phone }),
        ...(contactInfo?.email && { email: contactInfo.email }),
        ...(contactInfo?.address && { address: contactInfo.address }),
        ...(socialMedia && {
          sameAs: Object.values(socialMedia).filter(Boolean),
        }),
        logo: {
          "@type": "ImageObject",
          url:
            globalData?.defaultSeo?.image ||
            `${window.location.origin}/logo.png`,
        },
        url: window.location.origin,
      };
    }

    return enhancedSchema;
  }

  /**
   * Clear the cache (useful for testing or when global data is updated)
   */
  clearCache(): void {
    this.globalData = null;
    this.lastFetch = 0;
  }

  /**
   * Get cached global data without making a request
   */
  getCachedData(): GlobalSiteData | null {
    return this.globalData;
  }
}

// Export singleton instance
export const globalSEOService = new GlobalSEOService();

// Export the class for testing
export { GlobalSEOService };
